/**
 * kHashRoute — pure, env-neutral URL-hash ↔ route helpers for a single-page-app hash
 * router (direct `#` links + browser Back/Forward). Parsing and building the hash string
 * is plain, testable logic with no browser dependency, so it lives in the core layer
 * (getCoreUtils only) and unit-tests without a DOM. kHashRouter (in ReUsableTS) is the
 * thin browser shell that reads/writes window.location.hash and listens for hashchange
 * around these.
 *
 * The consumer supplies its own KnownSurfaces (the top-level route names it recognises);
 * anything outside that set parses to the root, so an unknown / stale / typed-garbage hash
 * never lands on a dead screen.
 *
 * Canonical hash form (built by kBuildHash):
 *   #/                        → root         (Surface "")
 *   #/<surface>               → a surface    (surface ∈ KnownSurfaces)
 *   #/<surface>/<sub>         → a sub-screen
 *   #/<surface>/<sub>/<arg>   → a sub-screen with an id
 *   any of the above + "?k=v&flag" → the same route with Params { k: "v", flag: true }
 *                               (a query is kept only on a known surface; 2026-10-23)
 *
 * Second hash form, key=value (kParseHashParams / kBuildHashParams), for pages that carry
 * state rather than a route:  #s=3&t=2&final&presenter  → { s: "3", t: "2", final: true,
 * presenter: true }.
 */

import { getCoreUtils } from "./kCoreHandler";

/** A parsed route. Surface "" = root. Sub/Arg are "" when absent. */
export interface IkHashRoute {
    Surface: string;
    Sub: string;
    Arg: string;
    /** The "?key=value&flag" part of the hash (kParseHashParams form). Absent when the hash
     *  has no query; kBuildHash writes it back when it has at least one key. */
    Params?: IkHashParams;
}

/** Parse a raw URL hash ("#/admin/jobs") into a route. Empty / unknown surface → root. */
export function kParseHash(Raw: string, KnownSurfaces: string[]): IkHashRoute {
    const Empty: IkHashRoute = { Surface: "", Sub: "", Arg: "" };
    if (getCoreUtils().KErrorMode === true) { return Empty; }
    try {
        if (typeof Raw !== "string") { return Empty; }
        let Text: string = Raw;
        if (Text.indexOf("#") === 0) { Text = Text.substring(1); }
        if (Text.indexOf("/") === 0) { Text = Text.substring(1); }
        if (Text === "") { return Empty; }
        const QueryAt: number = Text.indexOf("?");
        let Query: string = "";
        if (QueryAt >= 0) { Query = Text.substring(QueryAt + 1); Text = Text.substring(0, QueryAt); }
        const Parts: string[] = Text.split("/");
        const Surface: string = kDecodeSegment(Parts[0]);
        if (KnownSurfaces.indexOf(Surface) < 0) { return Empty; }
        let Sub: string = "";
        if (Parts.length > 1) { Sub = kDecodeSegment(Parts[1]); }
        // The arg is the remainder joined back (ids carry no "/", but joining is safe
        // and future-proof).
        let Arg: string = "";
        if (Parts.length > 2) { Arg = kDecodeSegment(Parts.slice(2).join("/")); }
        const Route: IkHashRoute = { Surface: Surface, Sub: Sub, Arg: Arg };
        if (Query !== "") { Route.Params = kParseHashParams(Query); }
        return Route;
    } catch (e) {
        getCoreUtils().KGlobalErrorHandler(e, "kHashRoute.kParseHash");
        return Empty;
    }
}

/** Build the canonical URL hash ("#/admin/jobs", "#/search?q=x") for a route. An empty /
 *  unknown surface yields the root hash "#/". */
export function kBuildHash(Route: IkHashRoute, KnownSurfaces: string[]): string {
    if (getCoreUtils().KErrorMode === true) { return "#/"; }
    try {
        if (Route.Surface === "" || KnownSurfaces.indexOf(Route.Surface) < 0) { return "#/"; }
        let Hash: string = "#/" + kEncodeSegment(Route.Surface);
        if (Route.Sub !== "") { Hash = Hash + "/" + kEncodeSegment(Route.Sub); }
        if (Route.Sub !== "" && Route.Arg !== "") { Hash = Hash + "/" + kEncodeSegment(Route.Arg); }
        if (Route.Params === undefined) { return Hash; }
        const Query: string = kBuildHashParams(Route.Params).substring(1);
        if (Query !== "") { Hash = Hash + "?" + Query; }
        return Hash;
    } catch (e) {
        getCoreUtils().KGlobalErrorHandler(e, "kHashRoute.kBuildHash");
        return "#/";
    }
}

/** Re-express a raw hash in canonical form (so "", "#", "#/" and unknown surfaces all
 *  collapse to the root hash "#/"). Used to compare "am I already here" without
 *  duplicating history / looping. */
export function kCanonicalHash(Raw: string, KnownSurfaces: string[]): string {
    if (getCoreUtils().KErrorMode === true) { return "#/"; }
    try {
        return kBuildHash(kParseHash(Raw, KnownSurfaces), KnownSurfaces);
    } catch (e) {
        getCoreUtils().KGlobalErrorHandler(e, "kHashRoute.kCanonicalHash");
        return "#/";
    }
}

/** A parsed key=value hash: a key with a value maps to its (decoded) string; a bare flag
 *  ("#final") maps to `true`. */
export interface IkHashParams {
    [Key: string]: string | true;
}

/** What kBuildHashParams accepts: string / number values become key=value, `true` becomes a
 *  bare flag, `false` / null / undefined omit the key. */
export interface IkHashParamsInput {
    [Key: string]: string | number | boolean | null | undefined;
}

/**
 * Parse a key=value hash ("#s=3&t=2&final&presenter") into a typed map. Flags without a
 * value become `true`; "k=" yields ""; a repeated key keeps the last value; empty segments
 * are skipped; %-sequences are decoded (a malformed one is kept raw). A leading "#" (and an
 * optional "?") is stripped.
 *
 * A second consumer's variation is an argument: pass KnownKeys to keep only the keys it
 * recognises (default [] = keep every key). The route form ("#/surface/sub", kParseHash) and
 * this form are independent; use one per hash.
 */
export function kParseHashParams(Raw: string, KnownKeys: string[] = []): IkHashParams {
    const Result: IkHashParams = {};
    if (getCoreUtils().KErrorMode === true) { return Result; }
    try {
        if (typeof Raw !== "string") { return Result; }
        let Text: string = Raw;
        if (Text.indexOf("#") === 0) { Text = Text.substring(1); }
        if (Text.indexOf("?") === 0) { Text = Text.substring(1); }
        if (Text === "") { return Result; }
        const Segments: string[] = Text.split("&");
        for (let Index: number = 0; Index < Segments.length; Index++) {
            kAddHashParam(Result, Segments[Index], KnownKeys);
        }
        return Result;
    } catch (e) {
        getCoreUtils().KGlobalErrorHandler(e, "kHashRoute.kParseHashParams");
        return {};
    }
}

/**
 * Build the hash ("#s=3&t=2&final") for a params map; the inverse of kParseHashParams.
 * `true` → bare flag, string / number → key=value (encoded), `false` / null / undefined →
 * omitted. Keys keep insertion order. No params yields "#".
 */
export function kBuildHashParams(Params: IkHashParamsInput): string {
    if (getCoreUtils().KErrorMode === true) { return "#"; }
    try {
        const Parts: string[] = [];
        const Keys: string[] = Object.keys(Params);
        for (let Index: number = 0; Index < Keys.length; Index++) {
            const Part: string = kBuildHashParam(Keys[Index], Params[Keys[Index]]);
            if (Part !== "") { Parts.push(Part); }
        }
        return "#" + Parts.join("&");
    } catch (e) {
        getCoreUtils().KGlobalErrorHandler(e, "kHashRoute.kBuildHashParams");
        return "#";
    }
}

function kAddHashParam(Result: IkHashParams, Segment: string, KnownKeys: string[]): void {
    if (getCoreUtils().KErrorMode === true) { return; }
    try {
        if (Segment === "") { return; }
        const EqualsAt: number = Segment.indexOf("=");
        let Key: string = Segment;
        if (EqualsAt >= 0) { Key = Segment.substring(0, EqualsAt); }
        Key = kDecodeSegment(Key);
        if (Key === "" || Key === "__proto__") { return; }
        if (KnownKeys.length > 0 && KnownKeys.indexOf(Key) < 0) { return; }
        if (EqualsAt < 0) { Result[Key] = true; return; }
        Result[Key] = kDecodeSegment(Segment.substring(EqualsAt + 1));
    } catch (e) {
        getCoreUtils().KGlobalErrorHandler(e, "kHashRoute.kAddHashParam");
    }
}

function kBuildHashParam(Key: string, Value: string | number | boolean | null | undefined): string {
    if (getCoreUtils().KErrorMode === true) { return ""; }
    try {
        if (Key === "" || Value === undefined || Value === null || Value === false) { return ""; }
        if (Value === true) { return kEncodeSegment(Key); }
        return kEncodeSegment(Key) + "=" + kEncodeSegment(String(Value));
    } catch (e) {
        getCoreUtils().KGlobalErrorHandler(e, "kHashRoute.kBuildHashParam");
        return "";
    }
}

function kEncodeSegment(Value: string): string {
    if (getCoreUtils().KErrorMode === true) { return ""; }
    try {
        return encodeURIComponent(Value);
    } catch (e) {
        getCoreUtils().KGlobalErrorHandler(e, "kHashRoute.kEncodeSegment");
        return "";
    }
}

function kDecodeSegment(Value: string): string {
    if (getCoreUtils().KErrorMode === true) { return ""; }
    try {
        return decodeURIComponent(Value);
    } catch (e) {
        // ERROR-SUPPRESSED-JUSTIFIED: a malformed %-sequence in the URL hash is expected
        // user/paste input, not an app fault — keep the raw token (it simply won't match a
        // known surface/sub) instead of reporting a user-typed URL as an error.
        return Value;
    }
}
